<script lang="ts">
/** One selectable value of the column being filtered. */
export interface GaiaFilterValue {
  key: string;
  label: string;
  /** How many rows carry it. */
  count?: string | number;
  checked: boolean;
}
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaSearchInput from '../base/inputs/GaiaSearchInput.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaNeutral, gaiaShadow, gaiaTextButtonOverrides } from '../themes/tokens';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';

const props = defineProps({
  values: {
    type: Array as PropType<GaiaFilterValue[]>,
    required: true,
  },
  /** v-model:search — the caller filters `values`, so it owns the term. */
  search: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: 'Search values',
  },
  /** Footer line, e.g. "2 selected". Omit to hide the footer. */
  pickedLabel: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  'update:search': [value: string];
  toggle: [key: string];
  clear: [];
}>();

const divider = gaiaNeutral.borderSubtle;
const surface = gaiaNeutral.surface;
const menuShadow = gaiaShadow.menu;
const hover = gaiaNeutral.surfaceHover;
const text = gaiaNeutral.text;
const ink = gaiaNeutral.ink;
// Each value is a <button> whose colour flips on :hover, so its type comes
// through v-bind rather than a GaiaText prop.
const optionSize = gaiaFontSize.dense;
</script>

<template>
  <!-- The panel behind a GaiaFilterChip: search, the column's values with their
       row counts, and a footer that says how many are picked. Presentational —
       host it in a GaiaPopover; the caller filters the list by `search`. -->
  <div class="gaia-filter-value-panel">
    <gaia-search-input
      :value="props.search"
      variant="panel"
      :placeholder="props.searchPlaceholder"
      :style="{ margin: '2px 2px 6px' }"
      @update:value="(value: string) => emit('update:search', value)"
    />

    <div class="options">
      <button
        v-for="value in props.values"
        :key="value.key"
        type="button"
        class="option"
        @click="emit('toggle', value.key)"
      >
        <gaia-checkbox
          :checked="value.checked"
          size="small"
          tabindex="-1"
          style="pointer-events: none"
        />
        <span class="label">{{ value.label }}</span>
        <gaia-text
          v-if="value.count !== undefined"
          class="count"
          size="micro"
          tone="muted"
          :style="{ fontVariantNumeric: 'tabular-nums' }"
        >
          {{ value.count }}
        </gaia-text>
      </button>
      <gaia-text v-if="!props.values.length" class="empty" size="dense" tone="muted">
        No values match “{{ props.search }}”
      </gaia-text>
    </div>

    <n-flex v-if="props.pickedLabel" align="center" :size="8" class="foot">
      <gaia-text size="meta" tone="muted">{{ props.pickedLabel }}</gaia-text>
      <gaia-button
        text
        size="tiny"
        :theme-overrides="gaiaTextButtonOverrides.ink"
        :style="{ marginLeft: 'auto' }"
        @click="emit('clear')"
      >
        Clear
      </gaia-button>
    </n-flex>
  </div>
</template>

<style scoped>
/* GaiaPopover is `raw`, so the panel supplies its own surface. */
.gaia-filter-value-panel {
  width: 276px;
  display: flex;
  flex-direction: column;
  padding: 6px;
  border-radius: 10px;
  background: v-bind(surface);
  box-shadow: v-bind(menuShadow);
}

/* The list scrolls; the search and footer stay put. */
.options {
  max-height: 252px;
  overflow-y: auto;
}

.option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  font-size: v-bind(optionSize);
  color: v-bind(text);
  text-align: left;
  cursor: pointer;
}

.option:hover {
  background: v-bind(hover);
  color: v-bind(ink);
}

.label {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.count {
  flex: none;
}

.empty {
  display: block;
  padding: 12px 10px;
}

.foot {
  margin-top: 6px;
  padding: 8px 10px 4px;
  border-top: 1px solid v-bind(divider);
}
</style>
